import type { SmeCompanyView, User } from '@shared/contracts/entities';
import type { SmeFeatureFlags } from '@shared/contracts/featureFlags';
import { SME_PERMISSIONS, type SmePermission } from '@shared/contracts/permissions';
import { nowMs } from './clock';

/**
 * The canonical entitlement chain.
 *
 * Binding rule, repeated across 99 supplemental checks and
 * quality/SECURITY_PRIVACY_AI_AND_ACCESSIBILITY.md:
 *
 *   tenant purchased AND bank/programme enabled AND company enabled AND user permitted
 *
 * Two further binding rules are encoded here:
 *
 *  - XS-SMEM-TT-R006-A2: a feature the user may not access is HIDDEN, not
 *    greyed out. Only `company-restricted` is surfaced with an explanation,
 *    because the requirements explicitly ask for restricted companies to
 *    explain which features are affected.
 *  - Scheduled permissions honour `permissionValidFrom` / `permissionValidUntil`
 *    against the deterministic clock, so expired access disappears immediately.
 */

export type DenialReason =
  | 'tenant-not-purchased'
  | 'programme-disabled'
  | 'company-module-disabled'
  | 'company-restricted'
  | 'company-read-only'
  | 'feature-flag-off'
  | 'permission-denied'
  | 'permission-expired';

/**
 * `hidden`    — remove the entry point entirely; a deep link yields a neutral
 *               restricted screen that does not confirm the feature exists.
 * `explained` — keep it visible and say why it cannot be used right now.
 */
export type DenialVisibility = 'hidden' | 'explained';

export type EntitlementDecision =
  | { readonly allowed: true }
  | {
      readonly allowed: false;
      readonly reason: DenialReason;
      readonly visibility: DenialVisibility;
      readonly message: string;
    };

export const ALLOWED: EntitlementDecision = { allowed: true };

/** Module keys. These are the units a tenant purchases and a programme enables. */
export const MODULES = {
  accounts: 'accounts',
  transactions: 'transactions',
  beneficiaries: 'beneficiaries',
  payments: 'payments',
  approvals: 'approvals',
  cards: 'cards',
  fx: 'fx',
  reports: 'reports',
  team: 'team',
  expense: 'expense',
  ai: 'ai',
  personalisation: 'personalisation',
  documents: 'documents',
  tax: 'tax',
  accounting: 'accounting',
  invoicing: 'invoicing',
  digitalAssets: 'digital-assets',
  notifications: 'notifications',
} as const;

export type ModuleKey = (typeof MODULES)[keyof typeof MODULES];

export type Tenant = {
  readonly id: string;
  readonly name: string;
  readonly purchasedModules: readonly ModuleKey[];
  readonly brandTokens: Readonly<Record<string, string>>;
};

export type Programme = {
  readonly id: string;
  readonly name: string;
  readonly tenantId: string;
  readonly enabledModules: readonly ModuleKey[];
  /** Domestic scheme wording differs by programme; never invented at render time. */
  readonly domesticScheme: 'uk-faster-payments' | 'sepa';
};

export type EntitlementContext = {
  readonly tenant: Tenant;
  readonly programme: Programme;
  readonly company: SmeCompanyView;
  readonly user: User;
  readonly featureFlags: SmeFeatureFlags;
};

/**
 * A capability is one gated unit of behaviour. Everything the UI shows or lets
 * a user do resolves through exactly one of these, so navigation and actions
 * can never disagree about entitlement.
 */
export type Capability = {
  readonly id: string;
  readonly module: ModuleKey;
  /** Feature flag that must be true, when the module is flag-gated. */
  readonly featureFlag?: keyof SmeFeatureFlags;
  /** All listed permissions must be held. */
  readonly permissions?: readonly SmePermission[];
  /** True when the capability writes data, so a read-only company blocks it. */
  readonly mutating?: boolean;
};

const P = SME_PERMISSIONS;

export const CAPABILITIES = {
  accountsView: { id: 'accountsView', module: MODULES.accounts, featureFlag: 'accounts', permissions: [P.accountsView] },
  accountsViewSensitive: { id: 'accountsViewSensitive', module: MODULES.accounts, featureFlag: 'accounts', permissions: [P.accountsViewSensitiveDetails] },
  accountsManage: { id: 'accountsManage', module: MODULES.accounts, featureFlag: 'accounts', permissions: [P.accountsView, P.accountsViewSensitiveDetails], mutating: true },

  transactionsView: { id: 'transactionsView', module: MODULES.transactions, featureFlag: 'transactions', permissions: [P.transactionsView] },
  transactionsExport: { id: 'transactionsExport', module: MODULES.transactions, featureFlag: 'transactions', permissions: [P.transactionsExport] },

  beneficiariesView: { id: 'beneficiariesView', module: MODULES.beneficiaries, featureFlag: 'beneficiaries', permissions: [P.beneficiariesView] },
  beneficiariesCreate: { id: 'beneficiariesCreate', module: MODULES.beneficiaries, featureFlag: 'beneficiaries', permissions: [P.beneficiariesCreate], mutating: true },
  beneficiariesManage: { id: 'beneficiariesManage', module: MODULES.beneficiaries, featureFlag: 'beneficiaries', permissions: [P.beneficiariesManage], mutating: true },

  paymentsView: { id: 'paymentsView', module: MODULES.payments, featureFlag: 'payments', permissions: [P.transactionsView] },
  paymentsCreate: { id: 'paymentsCreate', module: MODULES.payments, featureFlag: 'payments', permissions: [P.paymentsCreate], mutating: true },
  paymentsSubmit: { id: 'paymentsSubmit', module: MODULES.payments, featureFlag: 'payments', permissions: [P.paymentsSubmit], mutating: true },
  paymentsCancel: { id: 'paymentsCancel', module: MODULES.payments, featureFlag: 'payments', permissions: [P.paymentsCancel], mutating: true },

  approvalsView: { id: 'approvalsView', module: MODULES.approvals, featureFlag: 'approvals', permissions: [P.paymentsApprove] },
  approvalsDecide: { id: 'approvalsDecide', module: MODULES.approvals, featureFlag: 'approvals', permissions: [P.paymentsApprove], mutating: true },

  cardsView: { id: 'cardsView', module: MODULES.cards, featureFlag: 'cards', permissions: [P.cardsView] },
  cardsManage: { id: 'cardsManage', module: MODULES.cards, featureFlag: 'cards', permissions: [P.cardsManage], mutating: true },
  cardsReveal: { id: 'cardsReveal', module: MODULES.cards, featureFlag: 'cards', permissions: [P.cardsReveal] },

  fxView: { id: 'fxView', module: MODULES.fx, featureFlag: 'fx', permissions: [P.accountsView] },

  reportsView: { id: 'reportsView', module: MODULES.reports, featureFlag: 'reports', permissions: [P.reportsView] },
  reportsExport: { id: 'reportsExport', module: MODULES.reports, featureFlag: 'reports', permissions: [P.reportsExport] },

  teamView: { id: 'teamView', module: MODULES.team, featureFlag: 'teamManagement', permissions: [P.teamView] },
  teamInvite: { id: 'teamInvite', module: MODULES.team, featureFlag: 'teamManagement', permissions: [P.teamInvite], mutating: true },

  expenseEntry: { id: 'expenseEntry', module: MODULES.expense, featureFlag: 'expenseEntryPoints', permissions: [P.transactionsView] },

  aiSummary: { id: 'aiSummary', module: MODULES.ai, featureFlag: 'aiFinancialSummary', permissions: [P.accountsView] },
  aiAttention: { id: 'aiAttention', module: MODULES.ai, featureFlag: 'aiAttention', permissions: [P.accountsView] },
  aiTransactionSearch: { id: 'aiTransactionSearch', module: MODULES.ai, featureFlag: 'aiTransactionSearch', permissions: [P.transactionsView] },
  aiPaymentPreparation: { id: 'aiPaymentPreparation', module: MODULES.ai, featureFlag: 'aiPaymentPreparation', permissions: [P.paymentsCreate] },

  personalisation: { id: 'personalisation', module: MODULES.personalisation, permissions: [P.accountsView], mutating: true },
  aiLayoutComposer: { id: 'aiLayoutComposer', module: MODULES.personalisation, featureFlag: 'aiFinancialSummary', permissions: [P.accountsView], mutating: true },

  documentsView: { id: 'documentsView', module: MODULES.documents, permissions: [P.accountsView] },
  taxView: { id: 'taxView', module: MODULES.tax, permissions: [P.reportsView] },
  accountingView: { id: 'accountingView', module: MODULES.accounting, permissions: [P.reportsView] },
  invoicingView: { id: 'invoicingView', module: MODULES.invoicing, permissions: [P.reportsView] },
  digitalAssetsView: { id: 'digitalAssetsView', module: MODULES.digitalAssets, permissions: [P.accountsView] },
  notificationsView: { id: 'notificationsView', module: MODULES.notifications, permissions: [P.accountsView] },
} as const satisfies Record<string, Capability>;

export type CapabilityId = keyof typeof CAPABILITIES;

/** Company statuses in which no write may proceed, but history stays readable. */
const READ_ONLY_COMPANY_STATUSES = new Set(['closed', 'suspended']);
/** Company statuses that keep the surface visible but explain the limitation. */
const RESTRICTED_COMPANY_STATUSES = new Set(['restricted', 'review', 'onboarding']);

function permissionWindowOpen(user: User): boolean {
  const t = nowMs();
  if (user.permissionValidFrom && t < new Date(user.permissionValidFrom).getTime()) return false;
  if (user.permissionValidUntil && t > new Date(user.permissionValidUntil).getTime()) return false;
  return true;
}

function deny(
  reason: DenialReason,
  visibility: DenialVisibility,
  message: string,
): EntitlementDecision {
  return { allowed: false, reason, visibility, message };
}

/**
 * Resolve one capability against the full chain.
 *
 * Order matters: the widest gate is evaluated first so the reason returned is
 * the outermost cause, and so a permission check never leaks the existence of
 * a module the tenant has not purchased.
 */
export function resolveCapability(
  ctx: EntitlementContext,
  capability: Capability,
): EntitlementDecision {
  // 1. Tenant purchased.
  if (!ctx.tenant.purchasedModules.includes(capability.module)) {
    return deny('tenant-not-purchased', 'hidden', 'This module is not part of your plan.');
  }

  // 2. Bank / programme enabled.
  if (!ctx.programme.enabledModules.includes(capability.module)) {
    return deny('programme-disabled', 'hidden', 'This module is not enabled for your programme.');
  }

  // 3. Company enabled.
  if (!ctx.company.enabledModules.includes(capability.module)) {
    return deny('company-module-disabled', 'hidden', 'This module is not enabled for your company.');
  }

  // 4. Feature flag.
  if (capability.featureFlag && !ctx.featureFlags[capability.featureFlag]) {
    return deny('feature-flag-off', 'hidden', 'This feature is currently switched off.');
  }

  // 5. User permission, including the scheduled validity window.
  const required = capability.permissions ?? [];
  if (required.length > 0) {
    if (!permissionWindowOpen(ctx.user)) {
      return deny(
        'permission-expired',
        'hidden',
        'Your access to this feature has expired. Ask an administrator to extend it.',
      );
    }
    const held = new Set(ctx.user.permissions);
    if (!required.every((p) => held.has(p))) {
      return deny('permission-denied', 'hidden', 'You do not have permission for this.');
    }
  }

  // 6. Company status. Evaluated last so it never masks a hidden-module reason.
  if (READ_ONLY_COMPANY_STATUSES.has(ctx.company.status)) {
    if (capability.mutating) {
      return deny(
        'company-read-only',
        'explained',
        ctx.company.statusReason ??
          `Your company is ${ctx.company.status}. You can view history but cannot make changes.`,
      );
    }
  } else if (RESTRICTED_COMPANY_STATUSES.has(ctx.company.status) && capability.mutating) {
    return deny(
      'company-restricted',
      'explained',
      ctx.company.statusReason ??
        'Some actions are unavailable while your company profile is being reviewed.',
    );
  }

  return ALLOWED;
}

export function can(ctx: EntitlementContext, id: CapabilityId): boolean {
  return resolveCapability(ctx, CAPABILITIES[id]).allowed;
}

export function decide(ctx: EntitlementContext, id: CapabilityId): EntitlementDecision {
  return resolveCapability(ctx, CAPABILITIES[id]);
}

/**
 * True when the entry point must be removed from navigation entirely.
 * An `explained` denial stays visible with its reason.
 */
export function isHidden(ctx: EntitlementContext, id: CapabilityId): boolean {
  const d = resolveCapability(ctx, CAPABILITIES[id]);
  return !d.allowed && d.visibility === 'hidden';
}

/** Effective permission list after the scheduled window is applied. */
export function effectivePermissions(user: User): readonly string[] {
  return permissionWindowOpen(user) ? user.permissions : [];
}
